// src/utilities.jsx
let sessionTimerId = null;
let warningTimerId = null;
let pollingTimerId = null;


import { parseISO, format } from 'date-fns';

// Helper to clear main session keys
export const clearLocalStorage = () => {
  const keysToPreserve = new Set(['redimdwm_remembered_user_name']);

  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('redimdwm_') && !keysToPreserve.has(key)) {
      localStorage.removeItem(key);
    }
  }
  );
};


export function decodeJwt(token) {
  try {
    const [, payloadBase64] = token.split('.');
    if (!payloadBase64) throw new Error('Invalid JWT structure');

    // Fix padding and convert base64url to base64
    const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');

    const json = atob(padded); // Decode base64 to string
    return JSON.parse(json);   // Parse JSON payload
  } catch {
    return null;
  }
}


export function isJwtExpired(token) {
  if (!token) return true;
  const decoded = decodeJwt(token);
  if (!decoded || typeof decoded?.exp !== 'number') return true;
  return decoded.exp * 1000 < Date.now(); // JWT exp is in seconds
}

// src/utilities.js

// ... (keep existing functions like clearLocalStorage, decodeJwt, isJwtExpired) ...

/**
 * Formats a date string or Date object into MM/DD/YYYY format.
 * @param {string | Date} dateInput - The date to format.
 * @returns {string} The formatted date string, or an empty string if input is invalid.
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return ''; // Handle null or undefined input

  try {
    const dateString = String(dateInput).trim();
    if (/^0000-00-00/.test(dateString)) return '';

    const date = /^\d{4}-\d{2}-\d{2}$/.test(dateString)
      ? parseISO(dateString)
      : new Date(dateInput);
    // Check if the date is valid after parsing
    if (isNaN(date.getTime())) {
      return ''; // Return empty string for invalid dates
    }
    // Use 'en-US' locale for MM/DD/YYYY format
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  } catch {
    return ''; // Return empty string on error
  }
};

/**
 * Calculates age based on a birthdate.
 * @param {string | Date} birthdateInput - The birthdate.
 * @returns {number | string} The calculated age in years, or 'N/A' if input is invalid.
 */
export const calculateAge = (birthdateInput) => {
  if (!birthdateInput) return 'N/A';

  try {
    const dateString = String(birthdateInput).trim();
    if (/^0000-00-00/.test(dateString)) return 'N/A';

    const birthDate = /^\d{4}-\d{2}-\d{2}$/.test(dateString)
      ? parseISO(dateString)
      : new Date(birthdateInput);
    if (isNaN(birthDate.getTime())) {
      return 'N/A';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--; // Decrement age if the birthday hasn't occurred yet this year
    }
    return age >= 0 ? age : 'N/A'; // Return age or 'N/A' if calculation results in negative
  } catch {
    return 'N/A';
  }
};

export function validatePassword(password) {
  if (!password || password.trim() === '') {
      return 'Password cannot be empty.';
  }

  if (password.length < 8) {
      return 'Password must be at least 8 characters.';
  }

  const lowered = password.toLowerCase();
  const commonPasswords = [
      'password', '12345678', '123456789', 'qwerty', 'abc123', 'iloveyou', 'admin', 'welcome', 'letmein'
  ];
  if (commonPasswords.includes(lowered)) {
      return 'Please choose a stronger password.';
  }

  const firstChar = password[0];
  if (password.split('').every(char => char === firstChar)) {
      return 'Password cannot be a single repeated character.';
  }

  return null; // ✅ Valid password
}



/**
 * Formats a Date object into a 'yyyy-MM-dd HH:mm:ss' string using UTC values.
 * @param {Date} date The Date object to format.
 * @returns {string} The formatted UTC date-time string.
 */
export const formatDateToUtcString = (date) => {
  if (!(date instanceof Date) || isNaN(date)) {
      // Handle invalid date input if necessary
      return '';
  }


  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = date.getUTCDate().toString().padStart(2, '0');
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatUtcToDateString = (date) => {
  if (!(date instanceof Date) || isNaN(date)) {
      // Handle invalid date input if necessary
      return '';
  }


  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = date.getUTCDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Helper to format ISO UTC string to local date and time for display
export function formatUtcToLocalDateTimeDisplay(isoString) {
  if (!isoString) return '';
  try {
      const utcDate = parseISO(isoString); // parse the string into a real Date
      return format(utcDate, 'PP p'); // 'p' is localized short time format
  } catch {
      return 'Invalid Time';
  }
};

export function formatUtcToLocalTimeDisplay(isoString) {
  if (!isoString) return '';
  try {
      const utcDate = parseISO(isoString); // parse the string into a real Date
      return format(utcDate, 'p'); // 'p' is localized short time format
  } catch {
      return 'Invalid Time';
  }
};

// Helper to convert ISO UTC string to local date
export function utcToLocal(utcDate) {
  if (!(utcDate instanceof Date)) {
    throw new Error('Invalid date passed to localToUtc');
  }

  const offsetMs = utcDate.getTimezoneOffset() * 60_000;
  const newDate = new Date(utcDate.getTime() - offsetMs);


  return newDate;
};

// src/utilities/utilities.js
export function localToUtc(localDate) {
  if (!(localDate instanceof Date) || isNaN(localDate)) {
    throw new Error('Invalid date provided to localToUtc');
  }
  let utcDate = new Date(
    localDate.getUTCFullYear(),
    localDate.getUTCMonth(),
    localDate.getUTCDate(),
    localDate.getUTCHours(),
    localDate.getUTCMinutes(),
    localDate.getUTCSeconds(),
    localDate.getMilliseconds()
  );
  return utcDate;
}

export function LocalStringToDate(utcString) {
  if (!utcString) return null
  try {
    // Correct parsing of local datetime from MySQL-style string
    const [datePart, timePart] = utcString.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);

    const localDate = new Date(year, month - 1, day, hour, minute, second); // interpreted in local time
    return localDate;
  } catch {
    return null;
  }
}

export function utcStringToDate(utcInputString) {
  
  if (!utcInputString) {return null};
  
  let utcString = "";
  utcString = (utcInputString.includes('Z') ? utcInputString : utcInputString+'Z');

  const utcDate = new Date(utcString.replace(" ","T"));
  return utcDate;
}

// Start timers
export function startSessionTimer(timeoutSeconds) {
  clearSessionTimer(); // Always clear before starting new timers
  window.showSessionTimeoutModal(false);
  if (!timeoutSeconds || timeoutSeconds <= 0) return;

  // for testing
  //const expireInMs = 360 * 1000;  // 6 minute sessions for testing
  const expireInMs = timeoutSeconds * 1000;
  const warningInMs = expireInMs - (5 * 60 * 1000); // 5 min before expiry

  if (warningInMs > 0) {
    warningTimerId = setTimeout(() => {
      if (typeof window.showSessionTimeoutModal === 'function') {
        window.showSessionTimeoutModal(true);
      }
    }, warningInMs);
  }

  sessionTimerId = setTimeout(() => {
    if (typeof window.logoutUser === 'function') {
      window.logoutUser(true);
    }
  }, expireInMs);

  pollingTimerId = setInterval(() => {
    if(window.location.pathname.includes('register')) return;
    if(!isJwtExpired(localStorage.getItem('redimdwm_jwt'))) {
      return;
    }

    if (typeof window.logoutUser === 'function') {
      window.logoutUser(true);
    }
  }, 10000);

}

// Clear timers
export function clearSessionTimer() {
  if (sessionTimerId) clearTimeout(sessionTimerId);
  if (warningTimerId) clearTimeout(warningTimerId);
  if (pollingTimerId) clearInterval(pollingTimerId);
  sessionTimerId = null;
  warningTimerId = null;
  pollingTimerId = null;
}
